'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var runtimeCore = require('@vue/runtime-core');
var dateFns = require('date-fns');
var vue = require('vue');

const cache = {};
const DEFAULT_OPTIONS = {
    month: "short",
    weekday: "short",
    firstDayOfWeek: 0,
};
/**
 * Create a locale config object.
 *
 * @param locale ISO 2-alpha language code
 * @param options { month, weekday, firstDayOfWeek }
 * @returns
 */
function createLocale(locale, options) {
    const config = { ...DEFAULT_OPTIONS, ...options };
    return {
        monthNames: getMonthNames(locale, config.month),
        weekdayNames: getWeekdayNames(locale, config.weekday, config.firstDayOfWeek),
        firstDayOfWeek: config.firstDayOfWeek,
    };
}
/**
 * Get the month names (Jan, Feb, etc) for a given locale.
 *
 * @param locale ISO 2-alpha language code
 * @param style long|short|narrow
 */
function getMonthNames(locale = 'en', style = 'long') {
    const names = cache[`month:${locale}:${style}`] || [];
    if (names.length)
        return names;
    const formatter = new Intl.DateTimeFormat(locale, { month: style });
    for (let i = 0; i < 12; i++) {
        const date = new Date(Date.UTC(2000, i, 1, 0, 0, 0));
        names.push(formatter.format(date));
    }
    return names;
}
/**
 * Get the weekday names (Mon, Tues, etc) for a given locale.
 *
 * Adjust styles, offset, etc.
 *
 * @param locale ISO 2-alpha language code
 * @param style long|short|narrow
 * @param offset For adjusting the week start day (0 sunday, 1 monday, ...)
 */
function getWeekdayNames(locale = 'en', style = 'long', offset = 0) {
    const names = cache[`week:${locale}:${style}:${offset}`] || [];
    if (names.length)
        return names;
    const formatter = new Intl.DateTimeFormat(locale, { weekday: style });
    for (let i = 1; i <= 7; i++) {
        // October 2000 starts on a Sunday.
        const date = new Date(Date.UTC(2000, 9, i + offset, 0, 0, 0));
        names.push(formatter.format(date));
    }
    return names;
}

var i18n = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createLocale: createLocale,
    getMonthNames: getMonthNames,
    getWeekdayNames: getWeekdayNames
});

var script$3 = runtimeCore.defineComponent({
    name: 'Calendar',
    emits: [
        'click',
        'hover',
        'update:month',
    ],
    props: {
        /**
         * Hide/show the month/year dropdowns.
         *
         * @type {boolean}
         */
        showDropdowns: {
            type: Boolean,
        },
        /**
         * Hide/show the next/previous buttons.
         *
         * @type {boolean}
         */
        showNavigation: {
            type: Boolean,
        },
        /**
         * Disable emitting events.
         *
         * @type {boolean}
         */
        disabled: {
            type: Boolean,
        },
        /**
         * Highlight this date or range.
         *
         * @type {start: Date, end: Date}
         */
        selection: {
            type: Object,
            required: true,
        },
        /**
         * Focus on this month.
         *
         * This is a model prop.
         *
         * @emits update:month
         * @type {Date}
         */
        month: {
            type: Date,
            default: () => new Date(),
        },
        /**
         * Apply some classes to each date - indexed by a date string.
         *
         * For example:
         * ```json
         * {
         *   "2020-01-01": "disabled",
         *   "2020-01-02": "available",
         *   "2020-01-03": "available"
         * }
         * ```
         */
        overlay: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Locale settings.
         *
         * @type {Object} default English
         *  - monthNames: `string[]`
         *  - weekdayNames: `string[]`
         *  - firstDayOfWeek: `number`
         */
        i18n: {
            type: Object,
            default: () => createLocale('en', {
                month: 'long',
                weekday: 'short',
                firstDayOfWeek: 1,
            }),
        },
    },
    setup(props, context) {
        const { i18n } = runtimeCore.toRefs(props);
        const selection = runtimeCore.reactive(props.selection);
        const month = runtimeCore.computed({
            get() {
                return props.month.getMonth();
            },
            set(value) {
                const date = new Date(props.month);
                date.setMonth(value);
                context.emit('update:month', date);
            }
        });
        const year = runtimeCore.computed({
            get() {
                return props.month.getFullYear();
            },
            set(value) {
                const date = new Date(props.month);
                date.setFullYear(value);
                context.emit('update:month', date);
            },
        });
        const calendar = runtimeCore.computed(() => {
            const firstDay = new Date(year.value, month.value, 1);
            const lastDate = dateFns.sub(firstDay, { months: 1 });
            const lastMonth = lastDate.getMonth();
            const lastYear = lastDate.getFullYear();
            const daysInLastMonth = new Date(lastYear, month.value, 0).getDate();
            const dayOfWeek = firstDay.getDay();
            const calendar = [];
            for (let i = 0; i < 6; i++) {
                calendar[i] = [];
            }
            let startDay = daysInLastMonth - dayOfWeek + 1 + i18n.value.firstDayOfWeek;
            if (startDay > daysInLastMonth) {
                startDay -= 7;
            }
            if (dayOfWeek === i18n.value.firstDayIndex) {
                startDay = daysInLastMonth - 6;
            }
            let curDate = new Date(lastYear, lastMonth, startDay, 12, 0, 0);
            for (let i = 0, col = 0, row = 0; i < 6 * 7; i++, col++, curDate.setDate(curDate.getDate() + 1)) {
                if (i > 0 && col % 7 === 0) {
                    col = 0;
                    row++;
                }
                calendar[row][col] = new Date(curDate.getTime());
            }
            return calendar;
        });
        function dayClass(date) {
            const now = new Date().setHours(0, 0, 0, 0);
            const today = dateFns.format(date, 'yyyy-MM-dd');
            const classes = {
                'off': !dateFns.isSameMonth(date, props.month),
                'weekend': dateFns.isWeekend(date),
                'today': dateFns.isToday(date),
                'time-travel': dateFns.isBefore(date, now),
                'start-range': dateFns.isSameDay(selection.start, date),
                'end-range': dateFns.isSameDay(selection.end, date),
                'in-range': (!selection.empty &&
                    selection.start &&
                    selection.end &&
                    dateFns.isWithinInterval(date, {
                        start: dateFns.min([selection.start, selection.end]),
                        end: dateFns.max([selection.start, selection.end]),
                    })),
            };
            if (props.overlay) {
                const names = props.overlay[today];
                // date => class name
                if (typeof names === 'string') {
                    classes[names] = true;
                }
                // date => [ class names ]
                else if (Array.isArray(names)) {
                    for (let name of names) {
                        classes[name] = true;
                    }
                }
            }
            return classes;
        }
        function onClickPrevious(event) {
            if (props.disabled)
                return;
            const date = dateFns.sub(props.month, { months: 1 });
            context.emit('update:month', date);
        }
        function onClickNext(event) {
            if (props.disabled)
                return;
            const date = dateFns.add(props.month, { months: 1 });
            context.emit('update:month', date);
        }
        function onClickDate(date) {
            if (props.disabled)
                return;
            context.emit('click', date);
        }
        function onHoverDate(date) {
            if (props.disabled)
                return;
            context.emit('hover', date);
        }
        return {
            month,
            year,
            calendar,
            dayClass,
            onClickPrevious,
            onClickNext,
            onClickDate,
            onHoverDate,
        };
    }
});

const _hoisted_1$3 = { class: "calendar" };
const _hoisted_2$3 = { class: "calendar__header" };
const _hoisted_3$1 = /*#__PURE__*/vue.createElementVNode("span", { class: "calendar__button calendar__button--previous" }, null, -1 /* HOISTED */);
const _hoisted_4 = [
  _hoisted_3$1
];
const _hoisted_5 = { key: 1 };
const _hoisted_6 = {
  key: 2,
  colspan: 5
};
const _hoisted_7 = { class: "calendar__dropdowns" };
const _hoisted_8 = ["value"];
const _hoisted_9 = {
  key: 3,
  colspan: 5
};
const _hoisted_10 = /*#__PURE__*/vue.createElementVNode("span", { class: "calendar__button calendar__button--next" }, null, -1 /* HOISTED */);
const _hoisted_11 = [
  _hoisted_10
];
const _hoisted_12 = { key: 5 };
const _hoisted_13 = { class: "calendar__weekdays" };
const _hoisted_14 = ["onClick", "onMouseover"];

function render$3(_ctx, _cache, $props, $setup, $data, $options) {
  return (vue.openBlock(), vue.createElementBlock("table", _hoisted_1$3, [
    vue.createElementVNode("thead", null, [
      vue.createElementVNode("tr", _hoisted_2$3, [
        (_ctx.showNavigation)
          ? (vue.openBlock(), vue.createElementBlock("th", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = (...args) => (_ctx.onClickPrevious && _ctx.onClickPrevious(...args))),
              tabindex: "0"
            }, _hoisted_4))
          : (vue.openBlock(), vue.createElementBlock("th", _hoisted_5)),
        (_ctx.showDropdowns)
          ? (vue.openBlock(), vue.createElementBlock("th", _hoisted_6, [
              vue.createElementVNode("div", _hoisted_7, [
                vue.withDirectives(vue.createElementVNode("select", {
                  class: "calendar__select-month",
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((_ctx.month) = $event))
                }, [
                  (vue.openBlock(true), vue.createElementBlock(vue.Fragment, null, vue.renderList(_ctx.i18n.monthNames, (name, index) => {
                    return (vue.openBlock(), vue.createElementBlock("option", {
                      key: index,
                      value: index
                    }, vue.toDisplayString(name), 9 /* TEXT, PROPS */, _hoisted_8))
                  }), 128 /* KEYED_FRAGMENT */))
                ], 512 /* NEED_PATCH */), [
                  [vue.vModelSelect, _ctx.month]
                ]),
                vue.withDirectives(vue.createElementVNode("input", {
                  type: "number",
                  class: "calendar__select-year",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((_ctx.year) = $event))
                }, null, 512 /* NEED_PATCH */), [
                  [vue.vModelText, _ctx.year]
                ])
              ])
            ]))
          : (vue.openBlock(), vue.createElementBlock("th", _hoisted_9, vue.toDisplayString(_ctx.i18n.monthNames[_ctx.month]) + " " + vue.toDisplayString(_ctx.year), 1 /* TEXT */)),
        (_ctx.showNavigation)
          ? (vue.openBlock(), vue.createElementBlock("th", {
              key: 4,
              onClick: _cache[3] || (_cache[3] = (...args) => (_ctx.onClickNext && _ctx.onClickNext(...args))),
              tabindex: "0"
            }, _hoisted_11))
          : (vue.openBlock(), vue.createElementBlock("th", _hoisted_12))
      ])
    ]),
    vue.createElementVNode("tbody", null, [
      vue.createElementVNode("tr", _hoisted_13, [
        (vue.openBlock(true), vue.createElementBlock(vue.Fragment, null, vue.renderList(_ctx.i18n.weekdayNames, (weekDay) => {
          return (vue.openBlock(), vue.createElementBlock("th", { key: weekDay }, vue.toDisplayString(weekDay), 1 /* TEXT */))
        }), 128 /* KEYED_FRAGMENT */))
      ]),
      (vue.openBlock(true), vue.createElementBlock(vue.Fragment, null, vue.renderList(_ctx.calendar, (dateRow, index) => {
        return (vue.openBlock(), vue.createElementBlock("tr", {
          class: "calendar__week",
          key: index
        }, [
          (vue.openBlock(true), vue.createElementBlock(vue.Fragment, null, vue.renderList(dateRow, (date, index) => {
            return (vue.openBlock(), vue.createElementBlock("td", {
              key: index,
              class: vue.normalizeClass(_ctx.dayClass(date)),
              onClick: $event => (_ctx.onClickDate(date)),
              onMouseover: $event => (_ctx.onHoverDate(date)),
              tabindex: "0"
            }, [
              vue.renderSlot(_ctx.$slots, "day", {
                date: date.getDate()
              }, () => [
                vue.createTextVNode(vue.toDisplayString(date.getDate()), 1 /* TEXT */)
              ])
            ], 42 /* CLASS, PROPS, HYDRATE_EVENTS */, _hoisted_14))
          }), 128 /* KEYED_FRAGMENT */))
        ]))
      }), 128 /* KEYED_FRAGMENT */))
    ])
  ]))
}

script$3.render = render$3;
script$3.__file = "src/components/Calendar.vue";

var script$2 = runtimeCore.defineComponent({
    name: 'Dropdown',
    emits: [
        'update:isOpen',
        'clear',
    ],
    props: {
        /**
         * The default input text.
         *
         * @type {string}
         */
        text: {
            type: String,
            required: true,
        },
        /**
         * Placeholder text when empty.
         *
         * @type {string}
         */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * Disable the dropdown.
         *
         * @type {boolean}
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Allow one to 'clear' the date field.
         */
        showClearButton: {
            type: Boolean,
            default: false,
        },
        /**
         * Open/close the dropdown.
         *
         * This is ignored if the 'openPosition' is inline.
         *
         * @emits update:isOpen
         * @type {boolean}
         */
        isOpen: {
            type: Boolean,
            required: true,
        },
        /**
         * Which way the picker opens.
         *
         * @type {string} auto|center|left|right|inline
         */
        openPosition: {
            type: String,
            default: "auto",
        },
    },
    setup(props, context) {
        const { isOpen, openPosition } = runtimeCore.toRefs(props);
        // The root element that wraps everything.
        const root = runtimeCore.ref(null);
        // RAF tick status.
        const resizeTick = runtimeCore.ref(false);
        // Re-check the 'auto' position in a loop. In cases where the element
        // has been moved but the window hasn't been resized. I don't want to
        // implement a big silly mutation observer, so this will do.
        const resizeTimer = runtimeCore.ref(0);
        // The dropdown position as determined by pure magic.
        const autoPosition = runtimeCore.ref('left');
        // This is the CSS class applied to the dropdown container.
        // When the 'openPosition' is 'auto' this is based on the client
        // position in relation to the viewport size.
        const modifierClass = runtimeCore.computed(() => (openPosition.value === 'auto'
            ? autoPosition.value
            : openPosition.value));
        // Check the auto-size calc when updating the position.
        runtimeCore.watch(openPosition, onResize);
        function hasFocus() {
            if (!root.value)
                return false;
            const host = root.value.getRootNode();
            // @ts-ignore
            return root.value.contains(host.activeElement);
        }
        /**
         * Open the picker when clicking the input field.
         */
        function onFocus() {
            if (props.disabled)
                return;
            if (isOpen.value)
                return;
            context.emit('update:isOpen', true);
        }
        /**
         * This is attached to 'focusout' on the root element because
         * (unlike 'blur') it bubbles.
         */
        function onBlur() {
            if (props.disabled)
                return;
            if (!isOpen.value)
                return;
            setTimeout(() => {
                if (hasFocus())
                    return;
                context.emit('update:isOpen', false);
            }, 100);
        }
        /**
         * Close the picker on ESC.
         */
        function onEscape(event) {
            if (props.disabled)
                return;
            if (!isOpen.value)
                return;
            if (isOpen.value && event.key === 'Escape') {
                context.emit('update:isOpen', false);
            }
        }
        /**
         * Clear the field.
         */
        function onClear(event) {
            if (props.disabled)
                return;
            event.stopPropagation();
            if (isOpen.value) {
                context.emit('update:isOpen', false);
            }
            context.emit('clear');
        }
        /**
         * Calculate the position - used if openPosition is 'auto'.
         */
        function onResize() {
            // Skip it all if we're not in 'auto' mode.
            if (openPosition.value !== 'auto')
                return;
            if (!resizeTick.value) {
                resizeTick.value = true;
                requestAnimationFrame(() => {
                    if (!root.value)
                        return;
                    const box = root.value.getBoundingClientRect();
                    const half = window.innerWidth / 2;
                    autoPosition.value = box.right > half ? 'right' : 'left';
                    resizeTick.value = false;
                });
            }
        }
        /**
         * Prevent clicks from escaping the dropdown.
         */
        function noop(event) {
            event.stopPropagation();
        }
        runtimeCore.onMounted(() => {
            document.addEventListener('keyup', onEscape);
            window.addEventListener('resize', onResize);
            onResize();
            // One more for good luck.
            window.setTimeout(onResize, 500);
            // Poll every 5 seconds.
            resizeTimer.value = window.setInterval(onResize, 5000);
        });
        runtimeCore.onUnmounted(() => {
            document.removeEventListener('keyup', onEscape);
            window.removeEventListener('resize', onResize);
            window.clearInterval(resizeTimer.value);
            resizeTimer.value = 0;
        });
        return {
            root,
            isOpen,
            modifierClass,
            onFocus,
            onBlur,
            onClear,
            noop,
        };
    },
});

const _hoisted_1$2 = ["value", "placeholder"];
const _hoisted_2$2 = ["aria-disabled"];

function render$2(_ctx, _cache, $props, $setup, $data, $options) {
  return (vue.openBlock(), vue.createElementBlock("div", {
    ref: "root",
    class: "date-picker",
    onFocusout: _cache[6] || (_cache[6] = (...args) => (_ctx.onBlur && _ctx.onBlur(...args))),
    onFocusin: _cache[7] || (_cache[7] = (...args) => (_ctx.onFocus && _ctx.onFocus(...args)))
  }, [
    vue.renderSlot(_ctx.$slots, "hidden"),
    vue.renderSlot(_ctx.$slots, "input", {
      onFocus: _ctx.onFocus,
      onBlur: _ctx.onBlur
    }, () => [
      vue.createElementVNode("input", {
        type: "text",
        class: "date-picker__input",
        readonly: "",
        value: _ctx.text,
        placeholder: _ctx.placeholder,
        onClick: _cache[0] || (_cache[0] = (...args) => (_ctx.onFocus && _ctx.onFocus(...args))),
        onFocus: _cache[1] || (_cache[1] = (...args) => (_ctx.onFocus && _ctx.onFocus(...args))),
        onBlur: _cache[2] || (_cache[2] = (...args) => (_ctx.onBlur && _ctx.onBlur(...args)))
      }, null, 40 /* PROPS, HYDRATE_EVENTS */, _hoisted_1$2),
      (_ctx.showClearButton)
        ? (vue.openBlock(), vue.createElementBlock("div", {
            key: 0,
            class: "date-picker__clear",
            role: "button",
            "aria-label": "Clear",
            "aria-disabled": _ctx.disabled ? 'true' : 'false',
            onClick: _cache[3] || (_cache[3] = (...args) => (_ctx.onClear && _ctx.onClear(...args)))
          }, null, 8 /* PROPS */, _hoisted_2$2))
        : vue.createCommentVNode("v-if", true)
    ]),
    (_ctx.isOpen && _ctx.openPosition !== 'inline')
      ? (vue.openBlock(), vue.createElementBlock("div", {
          key: 0,
          class: "date-picker__mask",
          onClick: _cache[4] || (_cache[4] = (...args) => (_ctx.onBlur && _ctx.onBlur(...args)))
        }))
      : vue.createCommentVNode("v-if", true),
    (_ctx.isOpen || _ctx.openPosition === 'inline')
      ? (vue.openBlock(), vue.createElementBlock("div", {
          key: 1,
          class: vue.normalizeClass({
                'date-picker__dropdown': true,
                ['date-picker__dropdown--' + _ctx.modifierClass]: true,
            }),
          tabindex: "0",
          onClick: _cache[5] || (_cache[5] = (...args) => (_ctx.noop && _ctx.noop(...args)))
        }, [
          vue.renderSlot(_ctx.$slots, "default")
        ], 2 /* CLASS */))
      : vue.createCommentVNode("v-if", true)
  ], 544 /* HYDRATE_EVENTS, NEED_PATCH */))
}

script$2.render = render$2;
script$2.__file = "src/components/Dropdown.vue";

/**
 * Normalise a date into a comparable time-less type.
 *
 * Flattens the hours component.
 *
 * E.g. `2022-02-05 12:34:56` becomes `2022-02-05 00:00:00`.
 */
function cleanTime(date) {
    date = new Date(date);
    date.setHours(0, 0, 0, 0);
    return date;
}
/**
 * Normalise a date into a comparable month-type date.
 *
 * Flattens the day + time components so that `'2022-02-05' == '2022-02-10'`.
 *
 * E.g. `2022-02-05 12:34:56` becomes `2022-02-01 00:00:00`.
 */
function cleanMonth(date) {
    date = new Date(date);
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
}
/**
 * Is this date range valid?
 *
 * That is, not empty (flag), not null, valid dates.
 */
function isValid(range) {
    return (!range.empty
        && !!range.start
        && !!range.end
        && !isNaN(+range.start)
        && !isNaN(+range.end));
}
/**
 * Are the start/end dates swapped?
 *
 * This ignores the time component. So `start: 4pm / end: 3pm` is still
 * considered valid and _not_ backwards.
 */
function isBackwards(range) {
    if (range.empty)
        return false;
    return cleanTime(range.start) > cleanTime(range.end);
}
/**
 * Is this range a single date?
 *
 * As in, start == end.
 *
 * This ignores the time component.
 */
function isSingle(range) {
    if (range.empty)
        return false;
    return +cleanTime(range.start) == +cleanTime(range.end);
}
function copyRange(range) {
    return {
        start: range.start ? new Date(range.start) : null,
        end: range.end ? new Date(range.end) : null,
        empty: range.empty,
    };
}

var script$1 = runtimeCore.defineComponent({
    name: 'DatePicker',
    components: {
        Calendar: script$3,
        Dropdown: script$2,
    },
    emits: [
        'update:modelValue',
        'update:month',
        'hover',
    ],
    props: {
        /**
         * The input name, of the hidden input.
         *
         * If empty/null/undefined, the hidden input will not be rendered.
         *
         * @type {string}
         */
        name: {
            type: String,
            default: '',
        },
        /**
         * Show the dropdowns for month and year selection above the calendar.
         *
         * @type {boolean}
         */
        showDropdowns: {
            type: Boolean,
            default: false,
        },
        /**
         * Show the navigation above the calendar.
         *
         * @type {boolean}
         */
        showNavigation: {
            type: Boolean,
            default: false,
        },
        /**
         * Allow one to 'clear' the date field.
         */
        showClearButton: {
            type: Boolean,
            default: false,
        },
        /**
         * Disabled state. The picker does not popup on click. The user not
         * able to change values.
         *
         * @type {boolean}
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Makes the picker readonly. The user can still view and navigate
         * the calendar popup but cannot changes values.
         *
         * @type {boolean}
         */
        readonly: {
            type: Boolean,
            default: false,
        },
        /**
         * Which way the picker opens.
         *
         * @type {string} auto|center|left|right|inline
         */
        openPosition: {
            type: String,
            default: "auto",
        },
        /**
         * Minimum date allowed to be selected.
         *
         * @type {Date}
         */
        minDate: {
            type: Date,
        },
        /**
         * Maximum date allowed to be selected.
         *
         * @type {Date}
         */
        maxDate: {
            type: Date,
        },
        /**
         * Placeholder text when empty.
         *
         * @type {string}
         */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * Apply some classes to each date - indexed by a date string.
         *
         * For example:
         * ```json
         * {
         *   "2020-01-01": "disabled",
         *   "2020-01-02": "available",
         *   "2020-01-03": "available"
         * }
         * ```
         *
         * @type {[key: string]: string}
         */
        overlay: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Prevent selection on a date when an overlay value has this class.
         *
         * @type {string[]} -- default `['disabled']`
         */
        overlayDisabled: {
            type: Array,
            default: () => (['disabled']),
        },
        /**
         * The currently selected date.
         *
         * This is a model prop.
         *
         * @emits update:modelValue
         * @type {Date}
         */
        modelValue: {
            type: Date,
        },
        /**
         * Object containing locale data used by the picker.
         *
         * @type {Object} default English
         *  - monthNames: `string[]`
         *  - weekdayNames: `string[]`
         *  - firstDayOfWeek: `number`
         */
        i18n: {
            type: Object,
            default: () => createLocale('en', {
                month: 'long',
                weekday: 'short',
                firstDayOfWeek: 1,
            }),
        },
    },
    setup(props, context) {
        const { modelValue, overlay, overlayDisabled, ..._props } = runtimeCore.toRefs(props);
        // This is the active selection. It's a laziness/convenience.
        // The calendar component only accepts a range object, so this is
        // synchronised to the modelValue.
        const selection = runtimeCore.reactive(modelValue.value
            ? {
                start: modelValue.value,
                end: modelValue.value,
                empty: false,
            }
            : {
                start: runtimeCore.markRaw(new Date),
                end: runtimeCore.markRaw(new Date),
                empty: true,
            });
        // This is the viewport month.
        // This changes as the user navigates the calendar.
        const month = runtimeCore.ref(cleanMonth(props.modelValue || runtimeCore.markRaw(new Date)));
        const isOpen = runtimeCore.ref(false);
        const minDate = runtimeCore.computed(() => (_props.minDate.value
            ? cleanTime(_props.minDate.value)
            : null));
        const maxDate = runtimeCore.computed(() => (_props.maxDate.value
            ? cleanTime(_props.maxDate.value)
            : null));
        // Ensure the viewport is greater than the min date.
        runtimeCore.watch(minDate, (value) => {
            if (value && value > month.value) {
                month.value = cleanMonth(value);
            }
        });
        // Ensure the viewport is smaller than the max date.
        runtimeCore.watch(maxDate, (value) => {
            if (value && month.value > value) {
                month.value = cleanMonth(value);
            }
        });
        // Jump the months view to the selection on open.
        runtimeCore.watch(isOpen, isOpen => {
            if (!isOpen)
                return;
            if (modelValue.value && modelValue.value > month.value) {
                month.value = cleanMonth(modelValue.value);
            }
            if (maxDate.value && month.value > maxDate.value) {
                month.value = cleanMonth(maxDate.value);
            }
            if (minDate.value && month.value <= minDate.value) {
                month.value = cleanMonth(minDate.value);
            }
        });
        // Update the selection when the modelValue changes.
        // Note we're mushing the single value into both the start/end fields.
        runtimeCore.watch(modelValue, (value) => {
            if (value) {
                selection.start = value;
                selection.end = value;
                selection.empty = false;
            }
            else {
                selection.empty = true;
            }
        });
        // Emit changes to the viewport.
        runtimeCore.watch(month, (value, old) => {
            if (+value == +old)
                return;
            const month = runtimeCore.markRaw(new Date(value));
            context.emit('update:month', month);
        });
        // The text shown in the UI input box.
        const inputText = runtimeCore.computed(() => (modelValue.value
            ? dateFns.format(modelValue.value, 'PP')
            : ''));
        // The form value for the given 'name' prop.
        // This is an ISO string.
        const inputValue = runtimeCore.computed(() => (modelValue.value
            ? dateFns.format(modelValue.value, 'yyyy-MM-dd')
            : ''));
        // User clicks on a date, emit a change to the modelValue.
        function onClickDate(value) {
            // This permits one to _see_ the calendar, but not change it.
            // Also don't close the picker, there's no reason to do that.
            if (props.readonly)
                return;
            // Disabled on a matching overlay key.
            // By default this is 'disabled'.
            const key = dateFns.format(value, 'yyyy-MM-dd');
            if (overlayDisabled.value.includes(overlay.value[key]))
                return;
            isOpen.value = false;
            context.emit('update:modelValue', runtimeCore.toRaw(value));
        }
        // Clear things.
        function onClear() {
            // No click, no close, no nothing.
            if (props.readonly)
                return;
            isOpen.value = false;
            // Clear the value.
            context.emit('update:modelValue', null);
        }
        return {
            selection,
            month,
            inputText,
            inputValue,
            isOpen,
            onClickDate,
            onClear,
        };
    },
});

const _hoisted_1$1 = ["name", "value"];
const _hoisted_2$1 = { class: "date-picker__calendars" };

function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Calendar = vue.resolveComponent("Calendar");
  const _component_Dropdown = vue.resolveComponent("Dropdown");

  return (vue.openBlock(), vue.createBlock(_component_Dropdown, {
    text: _ctx.inputText,
    placeholder: _ctx.placeholder,
    openPosition: _ctx.openPosition,
    disabled: _ctx.disabled,
    showClearButton: _ctx.showClearButton && !_ctx.selection.empty,
    onClear: _ctx.onClear,
    isOpen: _ctx.isOpen,
    "onUpdate:isOpen": _cache[2] || (_cache[2] = $event => ((_ctx.isOpen) = $event))
  }, {
    hidden: vue.withCtx(() => [
      (_ctx.name)
        ? (vue.openBlock(), vue.createElementBlock("input", {
            key: 0,
            type: "hidden",
            name: _ctx.name,
            value: _ctx.inputValue
          }, null, 8 /* PROPS */, _hoisted_1$1))
        : vue.createCommentVNode("v-if", true)
    ]),
    input: vue.withCtx(() => [
      vue.renderSlot(_ctx.$slots, "input")
    ]),
    default: vue.withCtx(() => [
      vue.renderSlot(_ctx.$slots, "header", { selection: _ctx.modelValue }),
      vue.createElementVNode("div", _hoisted_2$1, [
        vue.createVNode(_component_Calendar, {
          i18n: _ctx.i18n,
          selection: _ctx.selection,
          overlay: _ctx.overlay,
          showNavigation: _ctx.showNavigation,
          showDropdowns: _ctx.showDropdowns,
          disabled: _ctx.disabled,
          month: _ctx.month,
          "onUpdate:month": _cache[0] || (_cache[0] = $event => ((_ctx.month) = $event)),
          onClick: _ctx.onClickDate,
          onHover: _cache[1] || (_cache[1] = $event => (_ctx.$emit('hover', $event)))
        }, {
          day: vue.withCtx(() => [
            vue.renderSlot(_ctx.$slots, "day")
          ]),
          _: 3 /* FORWARDED */
        }, 8 /* PROPS */, ["i18n", "selection", "overlay", "showNavigation", "showDropdowns", "disabled", "month", "onClick"])
      ]),
      vue.renderSlot(_ctx.$slots, "header", { selection: _ctx.modelValue })
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["text", "placeholder", "openPosition", "disabled", "showClearButton", "onClear", "isOpen"]))
}

script$1.render = render$1;
script$1.__file = "src/components/DatePicker.vue";

var script = runtimeCore.defineComponent({
    name: 'DateRangePicker',
    components: {
        Dropdown: script$2,
        Calendar: script$3,
    },
    emits: [
        'update:modelValue',
        'update:month',
        'select:start',
        'select:finish',
        'hover',
    ],
    props: {
        /**
         * The input name, of the hidden input.
         *
         * If empty/null/undefined, the hidden input will not be rendered.
         *
         * @type {string[]}
         */
        name: {
            type: Array,
            default: () => [],
        },
        /**
         * Show the dropdowns for month and year selection above the calendars.
         *
         * @type {boolean}
         */
        showDropdowns: {
            type: Boolean,
            default: false,
        },
        /**
         * Show the navigation above each calendar.
         *
         * @type {boolean}
         */
        showNavigation: {
            type: Boolean,
            default: false,
        },
        /**
         * Allow one to 'clear' the date field.
         */
        showClearButton: {
            type: Boolean,
            default: false,
        },
        /**
         * Disabled state. If true picker do not popup on click.
         *
         * @type {boolean}
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Makes the picker readonly.
         *
         * @type {boolean}
         */
        readonly: {
            type: Boolean,
            default: false,
        },
        /**
         * Single picker mode.
         *
         * @type {boolean}
         */
        singleMode: {
            type: Boolean,
            default: false,
        },
        /**
         * Which way the picker opens.
         *
         * @type {string} auto|center|left|right|inline
         */
        openPosition: {
            type: String,
            default: "auto",
        },
        /**
         * Minimum date allowed to be selected.
         *
         * @type {Date}
         */
        minDate: {
            type: Date,
        },
        /**
         * Maximum date allowed to be selected.
         *
         * @type {Date}
         */
        maxDate: {
            type: Date,
        },
        /**
         * Placeholder text when empty.
         *
         * @type {string}
         */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * Apply some classes to each date - indexed by a date string.
         *
         * For example:
         * ```json
         * {
         *   "2020-01-01": "disabled",
         *   "2020-01-02": "available",
         *   "2020-01-03": "available"
         * }
         * ```
         *
         * @type {[key: string]: string}
         */
        overlay: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Prevent selection on a date when an overlay value has this class.
         *
         * @type {string[]} -- default `['disabled']`
         */
        overlayDisabled: {
            type: Array,
            default: () => ['disabled'],
        },
        /**
         * The currently selected date range.
         *
         * This is a model prop.
         *
         * @emits update:modelValue
         * @type {start: Date, end: Date}
         */
        modelValue: {
            type: Object,
            default: () => ({
                start: runtimeCore.markRaw(new Date()),
                end: runtimeCore.markRaw(new Date()),
                empty: true,
            }),
        },
        /**
         * Object containing locale data used by the picker.
         *
         * @type {Object} default English
         *  - monthNames: `string[]`
         *  - weekdayNames: `string[]`
         *  - firstDayOfWeek: `number`
         */
        i18n: {
            type: Object,
            default: () => createLocale('en', {
                month: 'long',
                weekday: 'short',
                firstDayOfWeek: 1,
            }),
        },
    },
    setup(props, context) {
        // const { minDate, maxDate, overlay, overlayDisabled } = toRefs(props);
        const { overlay, overlayDisabled, ..._props } = runtimeCore.toRefs(props);
        // One way observer of the modelValue.
        // This is already reactive because it's an object. Computed results
        // are then always stripped of their reactivity to keep us safe.
        const range = runtimeCore.computed(() => copyRange(props.modelValue));
        // The active selection range.
        // Separated from the 'range' to prevent reactive updates while we're
        // in our 'selection' state.
        const selection = runtimeCore.reactive(copyRange(props.modelValue));
        // The viewport month.
        // This changes as the user navigates the calendar.
        const firstMonth = runtimeCore.ref(cleanMonth(props.modelValue.start || props.minDate || new Date()));
        // Active is while the user is choosing their second date to complete
        // the 'date range'.
        const isSelecting = runtimeCore.ref(false);
        // The open state can be overridden by the selecting state. Essentially
        // forcing the picker to stay open while selecting.
        const isOpen = runtimeCore.ref(false);
        // Cancel selections.
        runtimeCore.watch(isOpen, isOpen => {
            if (!isOpen) {
                isSelecting.value = false;
                if (isValid(range.value)) {
                    Object.assign(selection, copyRange(range.value));
                }
                else {
                    selection.empty = true;
                }
            }
        });
        // The text show in the UI input box.
        const inputText = runtimeCore.computed(() => (isValid(range.value)
            ? (dateFns.format(range.value.start, 'PP') + ' - ' + dateFns.format(range.value.end, 'PP'))
            : ''));
        // The form values for the 'name' prop.
        // These are ISO strings: { start, end }.
        const inputValue = runtimeCore.computed(() => (isValid(range.value)
            ? { start: dateFns.format(range.value.start, 'yyyy-MM-dd'), end: dateFns.format(range.value.end, 'yyyy-MM-dd') }
            : { start: '', end: '', empty: true }));
        // Two-way binding for the second viewport value.
        // This keeps the state in one place but allows the second calendar
        // to move the viewport around the place.
        const secondMonth = runtimeCore.computed({
            get() {
                return dateFns.add(firstMonth.value, { months: 1 });
            },
            set(value) {
                firstMonth.value = dateFns.sub(value, { months: 1 });
            },
        });
        // Sanitized without the time component.
        const minDate = runtimeCore.computed(() => (_props.minDate.value
            ? cleanTime(_props.minDate.value)
            : null));
        // Sanitized without the time component.
        const maxDate = runtimeCore.computed(() => (_props.maxDate.value
            ? cleanTime(_props.maxDate.value)
            : null));
        // Clamp the viewport within the min/max dates.
        runtimeCore.watch([minDate, maxDate], ([minDate, maxDate]) => {
            if (minDate && minDate > firstMonth.value) {
                firstMonth.value = cleanMonth(minDate);
            }
            if (maxDate && maxDate <= firstMonth.value) {
                firstMonth.value = cleanMonth(maxDate);
            }
        });
        // Update the selection when the model changes.
        // This is a ONE-WAY binding.
        runtimeCore.watch(range, range => {
            if (isValid(range)) {
                Object.assign(selection, copyRange(range));
            }
            else {
                selection.empty = true;
            }
        }, { deep: true });
        // Jump the months view to the selection on open.
        runtimeCore.watch(isOpen, isOpen => {
            if (!isOpen)
                return;
            if (isValid(selection) && selection.start > firstMonth.value) {
                firstMonth.value = cleanMonth(selection.start);
            }
            if (maxDate.value && firstMonth.value > maxDate.value) {
                firstMonth.value = cleanMonth(maxDate.value);
            }
            if (minDate.value && firstMonth.value < minDate.value) {
                firstMonth.value = cleanMonth(minDate.value);
            }
        });
        // Emit changes to the viewport.
        runtimeCore.watch(firstMonth, (value, old) => {
            // Gotta compare milliseconds.
            if (+value == +old)
                return;
            const month = runtimeCore.markRaw(new Date(value));
            context.emit('update:month', month);
        });
        /**
         * Start or stop a selection.
         *
         * A stop selection will emit an update.
         */
        function onClickDate(value) {
            if (props.readonly)
                return;
            // Prevent clicks from disabled overlay dates.
            const key = dateFns.format(value, 'yyyy-MM-dd');
            if (overlayDisabled.value.includes(overlay.value[key]))
                return;
            // Prevent clicks from out-of-range dates.
            if (minDate.value && value < minDate.value)
                return;
            if (maxDate.value && value > maxDate.value)
                return;
            // Strip any reactive-ness from the value.
            value = runtimeCore.toRaw(value);
            // Start selection.
            if (!isSelecting.value) {
                isSelecting.value = true;
                selection.start = value;
                selection.end = value;
                selection.empty = false;
                context.emit('select:start', value);
            }
            // Still in selection - don't finish just move the start instead.
            else if (value < selection.start) {
                selection.start = value;
                selection.end = value;
                selection.empty = false;
                context.emit('select:start', value);
            }
            // Finish selection.
            else {
                selection.end = value;
                isSelecting.value = false;
                isOpen.value = false;
                context.emit('update:modelValue', copyRange(selection));
                context.emit('select:finish', value);
            }
        }
        /**
         * When in 'selection' update the end date, but don't emit.
         */
        function onHoverDate(value) {
            if (!props.readonly &&
                isSelecting.value &&
                value >= selection.start) {
                selection.end = value;
            }
            context.emit('hover', value);
        }
        /**
         * Clear the value.
         */
        function onClear() {
            if (props.readonly)
                return;
            selection.empty = true;
            const empty = {
                empty: true,
                start: null,
                end: null,
            };
            context.emit('update:modelValue', empty);
        }
        return {
            selection,
            firstMonth,
            secondMonth,
            isSelecting,
            isOpen,
            inputText,
            inputValue,
            onClickDate,
            onHoverDate,
            onClear,
        };
    },
});

const _hoisted_1 = ["name", "value"];
const _hoisted_2 = ["name", "value"];
const _hoisted_3 = { class: "date-picker__calendars" };

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Calendar = vue.resolveComponent("Calendar");
  const _component_Dropdown = vue.resolveComponent("Dropdown");

  return (vue.openBlock(), vue.createBlock(_component_Dropdown, {
    text: _ctx.inputText,
    placeholder: _ctx.placeholder,
    openPosition: _ctx.openPosition,
    disabled: _ctx.disabled,
    showClearButton: _ctx.showClearButton && !_ctx.selection.empty,
    onClear: _ctx.onClear,
    isOpen: _ctx.isOpen,
    "onUpdate:isOpen": _cache[2] || (_cache[2] = $event => ((_ctx.isOpen) = $event))
  }, {
    hidden: vue.withCtx(() => [
      (_ctx.name[0])
        ? (vue.openBlock(), vue.createElementBlock("input", {
            key: 0,
            type: "hidden",
            name: _ctx.name[0],
            value: _ctx.inputValue.start
          }, null, 8 /* PROPS */, _hoisted_1))
        : vue.createCommentVNode("v-if", true),
      (_ctx.name[1])
        ? (vue.openBlock(), vue.createElementBlock("input", {
            key: 1,
            type: "hidden",
            name: _ctx.name[1],
            value: _ctx.inputValue.end
          }, null, 8 /* PROPS */, _hoisted_2))
        : vue.createCommentVNode("v-if", true)
    ]),
    input: vue.withCtx(() => [
      vue.renderSlot(_ctx.$slots, "input")
    ]),
    default: vue.withCtx(() => [
      vue.renderSlot(_ctx.$slots, "header", {
        selection: _ctx.selection,
        isSelecting: _ctx.isSelecting
      }),
      vue.createElementVNode("div", _hoisted_3, [
        vue.createVNode(_component_Calendar, {
          i18n: _ctx.i18n,
          selection: _ctx.selection,
          overlay: _ctx.overlay,
          showNavigation: _ctx.showNavigation,
          showDropdowns: _ctx.showDropdowns,
          disabled: _ctx.disabled,
          month: _ctx.firstMonth,
          "onUpdate:month": _cache[0] || (_cache[0] = $event => ((_ctx.firstMonth) = $event)),
          onClick: _ctx.onClickDate,
          onHover: _ctx.onHoverDate
        }, {
          day: vue.withCtx(() => [
            vue.renderSlot(_ctx.$slots, "day")
          ]),
          _: 3 /* FORWARDED */
        }, 8 /* PROPS */, ["i18n", "selection", "overlay", "showNavigation", "showDropdowns", "disabled", "month", "onClick", "onHover"]),
        (!_ctx.singleMode)
          ? (vue.openBlock(), vue.createBlock(_component_Calendar, {
              key: 0,
              i18n: _ctx.i18n,
              selection: _ctx.selection,
              overlay: _ctx.overlay,
              showNavigation: _ctx.showNavigation,
              showDropdowns: _ctx.showDropdowns,
              disabled: _ctx.disabled,
              month: _ctx.secondMonth,
              "onUpdate:month": _cache[1] || (_cache[1] = $event => ((_ctx.secondMonth) = $event)),
              onClick: _ctx.onClickDate,
              onHover: _ctx.onHoverDate
            }, {
              day: vue.withCtx(() => [
                vue.renderSlot(_ctx.$slots, "day")
              ]),
              _: 3 /* FORWARDED */
            }, 8 /* PROPS */, ["i18n", "selection", "overlay", "showNavigation", "showDropdowns", "disabled", "month", "onClick", "onHover"]))
          : vue.createCommentVNode("v-if", true)
      ]),
      vue.renderSlot(_ctx.$slots, "header", {
        selection: _ctx.selection,
        isSelecting: _ctx.isSelecting
      })
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["text", "placeholder", "openPosition", "disabled", "showClearButton", "onClear", "isOpen"]))
}

script.render = render;
script.__file = "src/components/DateRangePicker.vue";

exports.Calendar = script$3;
exports.DatePicker = script$1;
exports.DateRangePicker = script;
exports.cleanMonth = cleanMonth;
exports.cleanTime = cleanTime;
exports.copyRange = copyRange;
exports.i18n = i18n;
exports.isBackwards = isBackwards;
exports.isSingle = isSingle;
exports.isValid = isValid;
